import styled from 'styled-components';
import { Button } from '../../components';
import { Disclaimer } from '../../components/Result/Result.styles';
import {
  AMZ_BASIL,
  AMZ_BLACK,
  AMZ_INFO_BG,
  AMZ_INFO_TXT,
  AMZ_SUCCESS,
} from '../../styles/colors';
import { IconFont } from '../../styles/font';
import {
  FONT_WEIGHT_NORMAL,
  FONT_WEIGHT_REGULAR,
} from '../../styles/typography';

export type IconType = '\\e913' | '\\eaa3' | '\\e9d9';
export type TextType = '#0f1111' | 'inherit';
export type NotificationVariant = 'success' | 'info';

export const ICON_TYPES = {
  SUCCESS: '\\e913' as IconType,
  INFO: '\\eaa3' as IconType,
  WARNING: '\\e9d9' as IconType,
} as const;

export const TEXT_TYPES = {
  REGULAR: '#0f1111' as TextType,
  INHERIT: 'inherit' as TextType,
} as const;

const MOBILE_BREAKPOINT = '768px';
const WARNING_COLOR = '#cc0c39';

const MOBILE_VIEW = `
  line-height: 22px;
  ${FONT_WEIGHT_REGULAR}
`;

const ICON_STYLES = `
  ${IconFont}
  font-family: 'rs-iconfont';
  font-size: 18px;
  font-style: normal;
`;

export const Icon = styled.i<{ type: IconType }>`
  &&& {
    :before {
      content: '${({ type }) => type}';
      color: inherit;
      ${ICON_STYLES}
    }
  }
`;

export const Notification = styled.div<{ variant: NotificationVariant }>`
  &&& {
    display: flex;
    gap: 8px;
    border-radius: 4px;
    padding: 10px;
    font-size: 12px !important;
    line-height: 16px !important;
    font-weight: 400;
    color: ${AMZ_BLACK};

    ${({ variant }) => variant === 'success' && `
      background-color: ${AMZ_BASIL};
      color: ${AMZ_SUCCESS};
      border: 1px solid ${AMZ_SUCCESS};
    `}

    ${({ variant }) => variant === 'info' && `
      background-color: ${AMZ_INFO_BG};
      color: ${AMZ_INFO_TXT};
      border: 1px solid ${AMZ_INFO_TXT};
    `}
  }
`;

export const Text = styled.span<{ type?: TextType }>`
  &&& {
    color: ${({ type }) => type || TEXT_TYPES.INHERIT};
  }
`;

export const Header = styled.div`
  &&& {
    display: flex;
    gap: 8px;
  }
`;

export const HeaderIcon = styled.i`
  &&& {
    :before {
      color: ${WARNING_COLOR};
      content: '${ICON_TYPES.WARNING}';
      ${ICON_STYLES}
      line-height: 24px;
      vertical-align: middle;

      @media (max-width: ${MOBILE_BREAKPOINT}) {
        ${MOBILE_VIEW}
      }
    }
  }
`;

export const HeaderTitle = styled.span`
  &&& {
    @media (max-width: ${MOBILE_BREAKPOINT}) {
      margin: 0 !important;
      ${MOBILE_VIEW}
    }
  }
`;

export const Body = styled.div`
  &&& {
    > span {
      line-height: 20px !important;
      font-size: 14px !important;
      ${FONT_WEIGHT_NORMAL}
    }
  }
`;

export const NotifiedTitle = styled.div`
  &&& {
    font-size: 20px;
    line-height: 28px;
  }
`;

export const NotifiedDescription = styled.div`
  &&& {
    line-height: 20px !important;
    font-size: 14px !important;
    ${FONT_WEIGHT_NORMAL}
    margin-top: 8px;
  }
`;

export const GetNotifiedContainer = styled.div`
  &&& {
    border-radius: 8px;
    padding: 20px;
    margin: 16px 0;

    > *:not(:first-child) {
      margin: 10px 0 0 0 !important;
    }
  }
`;

export const ActionButton = styled(Button)`
  &&& {
    line-height: 29px;
    font-size: 13px;
    ${FONT_WEIGHT_NORMAL}
    padding: 0;
  }
`;

export const PrivacyNoticeDisclaimer = styled(Disclaimer)`
  &&& {
    font-size: 10px !important;
    line-height: 16px !important;
    > * {
      font-size: inherit !important;
      line-height: inherit !important;
    }
  }
`;
